import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * `surface` (default) is a standalone Lifted card. `embedded` is for a table
 * that already sits inside a card, panel or dialog: same table geometry and
 * scroll, no second border/shadow/radius.
 *
 * `scrollClassName` goes on the inner scroll box; give it a max height
 * (e.g. `max-h-72`) for a capped list whose sticky header must keep working.
 */
export type DataTableShellVariant = 'surface' | 'embedded'

export type DataTableShellProps = React.HTMLAttributes<HTMLDivElement> & {
  footer?: React.ReactNode
  variant?: DataTableShellVariant
  scrollClassName?: string
}

const SURFACE_CLASSES =
  'rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] shadow-[var(--surface-lifted-shadow)]'

const TABLE_CLASSES =
  '[&_table]:w-full [&_table]:border-collapse [&_table]:text-[length:var(--table-font-size)] [&_thead]:bg-[var(--table-header-bg)] [&_thead]:text-[var(--table-header-text)] [&_thead_tr]:h-[var(--table-header-height)] [&_tbody_tr]:h-[var(--table-row-height-mobile)] sm:[&_tbody_tr]:h-[var(--table-row-height)] [&_tbody_tr]:border-t [&_tbody_tr]:border-[var(--table-divider)] [&_tbody_tr]:bg-[var(--table-row-bg)] [&_tbody_tr]:transition-colors [&_tbody_tr:hover]:bg-[var(--table-row-hover-bg)] [&_th]:px-[var(--table-cell-padding-x)] [:where(&)_th]:text-left [&_th]:font-semibold [&_td]:px-[var(--table-cell-padding-x)]'

export const DataTableShell = React.forwardRef<
  HTMLDivElement,
  DataTableShellProps
>(({ footer, variant = 'surface', scrollClassName, children, className, ...props }, ref) => (
  <div
    ref={ref}
    data-variant={variant}
    className={cn(
      'overflow-hidden',
      variant === 'surface' && SURFACE_CLASSES,
      TABLE_CLASSES,
      className,
    )}
    {...props}
  >
    <div className={cn('scroll-hint-x overflow-x-auto', scrollClassName)}>
      {children}
    </div>
    {footer && (
      <div className="border-t border-[var(--table-divider)] bg-[var(--table-row-bg)] p-[var(--table-cell-padding-x)]">
        {footer}
      </div>
    )}
  </div>
))
DataTableShell.displayName = 'DataTableShell'
